import React, { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import { StopCircle, Mic } from "lucide-react";
import dashboardService from "../../api";
import { getUserID } from '../../../auth/services/authService';
const TranscriptionWidget = ({ onTranscriptionComplete, onRecordingChange }) => {
  // State variables
  const [isRecording, setIsRecording] = useState(false);
  const [hasStartedRecording, setHasStartedRecording] = useState(false);
  const [transcription, setTranscription] = useState("");
  const [persistentLines, setPersistentLines] = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");

  // Refs
  const transcriptionEndRef = useRef(null);
  const transcriptionTextRef = useRef("");
  const lastFinalTextRef = useRef("");
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const intervalRef = useRef(null);
  const socketRef = useRef(null);
  const fileCountRef = useRef(0);


  useEffect(() => {
    if (transcriptionEndRef.current) {
      const container = transcriptionEndRef.current.closest('.transcription-content');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [persistentLines, transcription]);

  useEffect(() => {
    const socketUrl = dashboardService.getTranscriptionSocketUrl();

    socketRef.current = io(socketUrl, {
      path: '/socket.io',
      transports: ["websocket", "polling"],
      reconnectionAttempts: 5
    });

    const socket = socketRef.current;

    socket.on("transcription", (data) => {
      if (!data || !data.text) return;

      transcriptionTextRef.current += `\n${data.text}`;

      if (data.text.startsWith("##")) {
        setPersistentLines((prev) => [...prev, data.text]);
        setTranscription("");
      } else if (data.text.startsWith(">>")) {
        setTranscription(data.text);
      }
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleStartRecording = useCallback(async () => {
    if (isRecording) return;

    setIsRecording(true);
    setHasStartedRecording(true);
    setUploadStatus("");
    if (onRecordingChange) onRecordingChange(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      await audioContext.audioWorklet.addModule('/audio-processor.js');

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, 'audio-processor');

      workletNode.port.onmessage = (event) => {
        try {
          const inputData = event.data.audioData;
          const originalSampleRate = audioContext.sampleRate;
          const targetSampleRate = 16000;
          const downsampled = dashboardService.downsampleBuffer(inputData, originalSampleRate, targetSampleRate);
          const pcmBuffer = dashboardService.floatTo16BitPCM(downsampled);
          const binaryData = new Uint8Array(pcmBuffer);

          socketRef.current.emit("audio_stream", binaryData);
        } catch (err) {
          console.error("Error during audio processing:", err);
        }
      };

      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
      processorRef.current = workletNode;
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setIsRecording(false);
    }

    intervalRef.current = setInterval(() => {
      const currentText = (transcriptionTextRef.current + (transcription || ""))
        || lastFinalTextRef.current;

      if (currentText.trim()) {
        socketRef.current.emit("context_update", { text: currentText });
      }

      if (onTranscriptionComplete) {
        onTranscriptionComplete(currentText);
      }
    }, 3000);
  }, [isRecording, transcription, onTranscriptionComplete, onRecordingChange]);

  const handleStopRecording = useCallback(async () => {
    if (!isRecording) return;

    setIsRecording(false);
    if (onRecordingChange) onRecordingChange(false);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.emit("stop_audio");
    }

    if (transcription.trim()) {
      transcriptionTextRef.current += `\n${transcription}`;
    }

    const finalText = transcriptionTextRef.current;

    try {
      if (processorRef.current) {
        processorRef.current.port.postMessage({ terminate: true });
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      if (mediaStreamRef.current) {
        const tracks = mediaStreamRef.current.getTracks();
        tracks.forEach((track) => {
          track.stop();
        });
        mediaStreamRef.current = null;
      }

      if (audioContextRef.current) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }
    } catch (error) {
      console.error("Error during cleanup:", error);
    }

    lastFinalTextRef.current = finalText;

    if (onTranscriptionComplete && finalText.trim()) {
      onTranscriptionComplete(finalText);
    }

    if (finalText.trim()) {
      setUploadStatus("Uploading...");
      try {
        const result = await dashboardService.uploadTranscription(finalText, getUserID());
        if (result.success) {
          setUploadStatus("Upload complete");
        } else {
          setUploadStatus(`Upload failed: ${result.error}`);
        }
      } catch (error) {
        setUploadStatus(`Upload error: ${error.message}`);
      }

      setTimeout(() => setUploadStatus(""), 3000);
    }

    transcriptionTextRef.current = "";
  }, [isRecording, transcription, onTranscriptionComplete, onRecordingChange]);

  return (
    <div className="bg-white p-4 shadow-md shadow-blue-900/5 max-w-full relative rounded">
      <h3 className="font-semibold text-primaryDark mb-1">Live Transcription</h3>
      <div className="bg-gray-50 border border-gray-200 rounded p-3 h-[83vh] overflow-y-auto flex flex-col justify-between">
        <div className="flex-grow overflow-y-auto transcription-content">
          {persistentLines.map((line, index) => (
            <p
              key={`persistent-${index}-transcription`}
              className="my-1 leading-normal text-sm text-gray-700"
            >
              {line}
            </p>
          ))}

          {transcription && (
            <p
              key="interim-transcription"
              className="my-1 leading-normal text-sm text-gray-700"
            >
              {transcription}
            </p>
          )}

          <div ref={transcriptionEndRef} />
        </div>

        <div className="mt-3 flex justify-end">
          <button
            className={`flex items-center justify-center ${
              isRecording
                ? "bg-red-100 text-red-700 hover:bg-red-200"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            } border-none rounded py-2 px-3 text-sm font-medium cursor-pointer transition-all duration-200`}
            onClick={isRecording ? handleStopRecording : handleStartRecording}
          >
            {isRecording ? (
              <>
                <StopCircle size={16} className="mr-1.5" />
                Stop
              </>
            ) : (
              <>
                <Mic size={16} className="mr-1.5" />
                Record
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(TranscriptionWidget);
